/**
 * Where a program meets — "Kaleido Room", "Annex Room 2" — from the shared `locations`
 * catalog, referenced by `programs.location_id`.
 *
 * Optional: a program with no location simply shows none. Every surface that shows a
 * program's facts (card pills, calendar chips, roster headers) reads it through
 * `programLocationName` so an unset location and a missing embed look the same.
 */

export interface ProgramLocation {
  id: string;
  name: string;
}

/**
 * PostgREST embed for a program's location. Append to any `programs` select list:
 * `.select(\`id, name, ${PROGRAM_LOCATION_EMBED}\`)`. Many-to-one, so it comes back as one
 * object (or null), not an array.
 */
export const PROGRAM_LOCATION_EMBED = 'location:locations(id, name)';

/** The shape a program row carries once `PROGRAM_LOCATION_EMBED` is selected. */
export interface WithProgramLocation {
  /**
   * An array is tolerated because supabase-js types an untyped embed as one; PostgREST
   * itself returns a single object for this many-to-one relation.
   */
  location?: ProgramLocation | ProgramLocation[] | null;
}

/** The location's display name, or null when none is on file (or it was not selected). */
export function programLocationName(program: WithProgramLocation | null | undefined): string | null {
  const loc = program?.location;
  const row = Array.isArray(loc) ? loc[0] : loc;
  const name = row?.name?.trim();
  return name ? name : null;
}
